<template>
  <div class="container-fluid">
    <!-- Filter Section -->
    <div class="row mb-4">
      <div class="col-12">
        <div class="card">
          <div class="card-body">
            <div class="row g-3">
              <div class="col-md-3">
                <label class="form-label">Technician</label>
                <tech-ac v-model="filters.techId" />
              </div>
              <div class="col-md-3">
                <label class="form-label">Date Range Preset</label>
                <select 
                  class="form-select form-select-sm" 
                  v-model="datePreset"
                  @change="applyDatePreset"
                >
                  <option value="">Custom Range</option>
                  <option value="this-week">This Week</option>
                  <option value="last-week">Last Week</option>
                  <option value="this-pay-period">This Pay Period</option>
                  <option value="last-pay-period">Last Pay Period</option>
                  <option value="this-month">This Month</option>
                  <option value="last-month">Last Month</option>
                </select>
              </div>
              <div class="col-md-2">
                <label class="form-label">Start Date</label>
                <input 
                  type="date" 
                  class="form-control form-control-sm" 
                  v-model="filters.startDate"
                />
              </div>
              <div class="col-md-2">
                <label class="form-label">End Date</label>
                <input 
                  type="date" 
                  class="form-control form-control-sm" 
                  v-model="filters.endDate"
                />
              </div>
              <div class="col-md-2 d-flex align-items-end">
                <button 
                  class="btn btn-sm btn-outline-primary w-100" 
                  @click="loadTimesheet"
                  :disabled="!filters.techId || loading"
                >
                  <i class="fa fa-play me-2"></i>
                  {{ loading ? 'Loading...' : 'Load Timesheet' }}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Loading State -->
    <div v-if="loading" class="text-center py-5">
      <div class="spinner-border text-primary" role="status">
        <span class="visually-hidden">Loading...</span>
      </div>
      <p class="mt-2">Loading timesheet...</p>
    </div>

    <!-- Timesheet Content -->
    <div v-else-if="ranReport">
      <!-- Summary Cards -->
      <div class="row row-cols-1 row-cols-md-4 g-3 mb-4">
        <div class="col">
          <div class="card h-100 bg-light border-0">
            <div class="card-body text-center">
              <i class="fa fa-clock fa-2x text-primary mb-2"></i>
              <h6 class="text-muted mb-1">Total Hours</h6>
              <h3 class="mb-0">{{ totalHours.toFixed(1) }}</h3>
            </div>
          </div>
        </div>
        <div class="col">
          <div class="card h-100 bg-light border-0">
            <div class="card-body text-center">
              <i class="fa fa-briefcase fa-2x text-success mb-2"></i>
              <h6 class="text-muted mb-1">Work Hours</h6>
              <h3 class="mb-0">{{ workHours.toFixed(1) }}</h3>
            </div>
          </div>
        </div>
        <div class="col">
          <div class="card h-100 bg-light border-0">
            <div class="card-body text-center">
              <i class="fa fa-umbrella-beach fa-2x text-warning mb-2"></i>
              <h6 class="text-muted mb-1">PTO Hours</h6>
              <h3 class="mb-0">{{ ptoHours.toFixed(1) }}</h3>
            </div>
          </div>
        </div>
        <div class="col">
          <div class="card h-100 bg-light border-0">
            <div class="card-body text-center">
              <i class="fa fa-medkit fa-2x text-danger mb-2"></i>
              <h6 class="text-muted mb-1">Sick Hours</h6>
              <h3 class="mb-0">{{ sickHours.toFixed(1) }}</h3>
            </div>
          </div>
        </div>
      </div>

      <!-- No entries -->
      <div v-if="entries.length === 0" class="alert alert-info">
        <i class="fa fa-info-circle me-2"></i>
        No time entries found for the selected criteria.
      </div>

      <!-- Daily Breakdown -->
      <div v-else>
        <div v-for="(dayGroup, date) in groupedByDate" :key="date" class="mb-3">
          <!-- Day Header -->
          <div class="card border-0 shadow-sm mb-2">
            <div class="card-header bg-primary text-white d-flex justify-content-between align-items-center py-2">
              <h6 class="mb-0">
                <i class="fa fa-calendar me-2"></i>
                {{ formatDateHeader(String(date)) }}
              </h6>
              <span class="badge bg-light text-dark">
                {{ dayGroup.totalHours.toFixed(1) }} hours
              </span>
            </div>
          </div>

          <!-- Entries Table -->
          <div class="card border-0 shadow-sm">
            <div class="card-body p-0">
              <table class="table table-hover mb-0">
                <thead class="table-light">
                  <tr>
                    <th style="width: 15%;">Type</th>
                    <th style="width: 20%;">Clock In</th>
                    <th style="width: 20%;">Clock Out</th>
                    <th style="width: 15%; text-align: right;">Hours</th>
                    <th style="width: 15%;">Status</th>
                    <th style="width: 15%;">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="entry in dayGroup.entries" :key="entry.Id">
                    <td>
                      <span :class="getTypeBadge(entry.Type)">
                        {{ getTypeText(entry.Type) }}
                      </span>
                    </td>
                    <td>{{ formatTime(entry.ClockIn) }}</td>
                    <td>{{ entry.ClockOut ? formatTime(entry.ClockOut) : '—' }}</td>
                    <td class="text-end">
                      <strong>{{ getEntryHours(entry).toFixed(1) }}</strong>
                    </td>
                    <td>
                      <span v-if="entry.NeedsReview" class="badge bg-warning">Needs Review</span>
                      <span v-else-if="entry.IsManualEntry" class="badge bg-info">Manual</span>
                      <span v-else class="badge bg-success">OK</span>
                    </td>
                    <td>
                      <button 
                        class="btn btn-sm btn-outline-primary"
                        @click="editEntry(entry)"
                        title="Edit"
                      >
                        <i class="fa fa-edit"></i>
                      </button>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { ref, computed, onMounted } from 'vue';
import ApiService from '@/services/ApiService';
import TechAc from '@/components/Techs/TechAc.vue';
import { useAppStore } from '@/stores/AppStore';

const props = defineProps<{
  selectedTechIds: number[];
}>();

const appStore = useAppStore();

const filters = ref({
  techId: '' as string,
  startDate: '' as string,
  endDate: '' as string
});

const datePreset = ref('this-week');
const loading = ref(false);
const ranReport = ref(false);
const entries = ref<any[]>([]);

onMounted(() => {
  const userId = appStore.account.user?.UserId;
  if (userId) {
    filters.value.techId = String(userId);
  }
  applyDatePreset();
  loadTimesheet();
});

const applyDatePreset = () => {
  const today = new Date();
  const dayOfWeek = today.getDay();
  let startDate: Date, endDate: Date;

  switch (datePreset.value) {
    case 'this-week': {
      const diff = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
      startDate = new Date(today);
      startDate.setDate(today.getDate() - diff);
      endDate = new Date(startDate);
      endDate.setDate(startDate.getDate() + 6);
      break;
    }
    case 'last-week': {
      const diff = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
      startDate = new Date(today);
      startDate.setDate(today.getDate() - diff - 7);
      endDate = new Date(startDate);
      endDate.setDate(startDate.getDate() + 6);
      break;
    }
    case 'this-pay-period': {
      // Bi-weekly - use Monday as base
      const diff = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
      startDate = new Date(today);
      startDate.setDate(today.getDate() - diff);
      // Go back to the nearest pay period start (every 2 weeks)
      const weekNum = Math.floor((startDate.getTime() - new Date(startDate.getFullYear(), 0, 1).getTime()) / (7 * 24 * 60 * 60 * 1000));
      if (weekNum % 2 !== 0) {
        startDate.setDate(startDate.getDate() - 7);
      }
      endDate = new Date(startDate);
      endDate.setDate(startDate.getDate() + 13);
      break;
    }
    case 'last-pay-period': {
      const diff = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
      startDate = new Date(today);
      startDate.setDate(today.getDate() - diff);
      const weekNum = Math.floor((startDate.getTime() - new Date(startDate.getFullYear(), 0, 1).getTime()) / (7 * 24 * 60 * 60 * 1000));
      if (weekNum % 2 !== 0) {
        startDate.setDate(startDate.getDate() - 7);
      }
      startDate.setDate(startDate.getDate() - 14);
      endDate = new Date(startDate);
      endDate.setDate(startDate.getDate() + 13);
      break;
    }
    case 'this-month':
      startDate = new Date(today.getFullYear(), today.getMonth(), 1);
      endDate = new Date(today.getFullYear(), today.getMonth() + 1, 0);
      break;
    case 'last-month':
      startDate = new Date(today.getFullYear(), today.getMonth() - 1, 1);
      endDate = new Date(today.getFullYear(), today.getMonth(), 0);
      break;
    default:
      return;
  }

  filters.value.startDate = startDate.toISOString().split('T')[0];
  filters.value.endDate = endDate.toISOString().split('T')[0];
};

// Computed summary values
const totalHours = computed(() => entries.value.reduce((sum, e) => sum + getEntryHours(e), 0));
const workHours = computed(() => entries.value.filter(e => e.Type === 1).reduce((sum, e) => sum + getEntryHours(e), 0));
const ptoHours = computed(() => entries.value.filter(e => e.Type === 2).reduce((sum, e) => sum + getEntryHours(e), 0));
const sickHours = computed(() => entries.value.filter(e => e.Type === 4).reduce((sum, e) => sum + getEntryHours(e), 0));

// Group by date
const groupedByDate = computed(() => {
  const grouped: Record<string, { totalHours: number; entries: any[] }> = {};
  
  entries.value.forEach(entry => {
    const dateKey = new Date(entry.ClockIn).toISOString().split('T')[0];
    if (!grouped[dateKey]) {
      grouped[dateKey] = { totalHours: 0, entries: [] };
    }
    const hours = getEntryHours(entry);
    grouped[dateKey].entries.push(entry);
    grouped[dateKey].totalHours += hours;
  });

  const sorted: Record<string, { totalHours: number; entries: any[] }> = {};
  Object.keys(grouped).sort().forEach(key => {
    sorted[key] = grouped[key];
  });
  return sorted;
});

const getEntryHours = (entry: any): number => {
  if (entry.HoursOverride != null) return Number(entry.HoursOverride);
  if (!entry.ClockIn || !entry.ClockOut) return 0;
  const diff = new Date(entry.ClockOut).getTime() - new Date(entry.ClockIn).getTime();
  return diff / (1000 * 60 * 60);
};

const formatDateHeader = (dateStr: string): string => {
  const date = new Date(dateStr + 'T00:00:00');
  const today = new Date();
  const todayOnly = today.toISOString().split('T')[0];
  if (dateStr === todayOnly) {
    return 'Today - ' + date.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' });
  }
  return date.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' });
};

const formatTime = (dateStr: string): string => {
  if (!dateStr) return '';
  return new Date(dateStr).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
};

const getTypeBadge = (type: number): string => {
  switch (type) {
    case 1: return 'badge bg-primary';
    case 2: return 'badge bg-warning text-dark';
    case 3: return 'badge bg-info';
    case 4: return 'badge bg-danger';
    case 5: return 'badge bg-secondary';
    default: return 'badge bg-secondary';
  }
};

const getTypeText = (type: number): string => {
  switch (type) {
    case 1: return 'Work';
    case 2: return 'PTO';
    case 3: return 'Holiday';
    case 4: return 'Sick';
    case 5: return 'Unpaid';
    case 6: return 'Scheduled';
    default: return 'Unknown';
  }
};

const editEntry = (entry: any) => {
  // Will be connected to an edit modal later
  console.log('Edit entry:', entry.Id);
};

const loadTimesheet = async () => {
  if (!filters.value.techId) return;
  
  loading.value = true;
  ranReport.value = true;

  try {
    const filter: any = {
      UserId: [parseInt(filters.value.techId)],
      Page: 0,
      Size: 9999
    };
    if (filters.value.startDate) {
      filter.StartDate = filters.value.startDate + 'T00:00:00';
    }
    if (filters.value.endDate) {
      filter.EndDate = filters.value.endDate + 'T23:59:59';
    }

    const response = await ApiService.loadTimeEntries(filter, true);
    entries.value = response?.Data || [];
  } catch (error) {
    console.error('Error loading timesheet:', error);
    entries.value = [];
  } finally {
    loading.value = false;
  }
};
</script>
